import { useState, useEffect, useRef } from "react";
import { getCampaign_api } from "api/campaignApi";
import ButtonSymphony from "elements/ButtonSymphony";
import CardSymphony from "elements/CardSymphony";
import Loader from "elements/Loader";
import Colors from "modules/Colors";
import { useHistory, useParams } from "react-router-dom";
import Icon from "elements/Icon";
import { FacebookCampaignSubmissionType, YouTubeCampaignSubmissionType } from "modules/Campaign";
import { AdPlatform, CampaignStatus, CampaignType, PlaybookType, PLAYBOOK_TYPE_INFO } from "modules/Const";
import Sty from "modules/Sty";
import { isEmpty } from "modules/Utils";

interface RouteParams {
    campaignId: string;
}

const SuccessCampaign = () => {
    const history = useHistory();
    const { campaignId: campaignIdParam } = useParams<RouteParams>();
    const campaignId: number = Number(campaignIdParam)

    const [errorMessage, setErrorMessage] = useState<string>('');
    const [campaignStatus, setCampaignStatus] = useState<string>('');
    const [adPlatform, setAdPlatform] = useState<AdPlatform | null>(null); // NOTE CONNER: this is not used anywhere, BUT is possible useful as this page grows
    const [isLoadingCampaign, setIsLoadingCampaign] = useState<boolean>(true);
    const [campaign, setCampaign] = useState<any>(null);
    
    /** Refs to keep track of the latest state values */
    const campaignStatusRef = useRef(campaignStatus);
    
    // Update the refs when state changes
    useEffect(() => {
        campaignStatusRef.current = campaignStatus;
    }, [campaignStatus]);
    
    const getCampaignProcessingStatus = async () => {
        // NOTE: we are using the getCampaign_api to get the campaign status because we have to check Meta, TikTok, and YouTube for the campaign status
        // getCampaign ONYL is used for Meta and TikTik specifically until YouTube is added to the useCampaign store
        const campaignLocal = await getCampaign_api(campaignId)
        if (!campaignLocal) {
            return;
        }

        const campaignStatusLocal = campaignLocal?.status
        setCampaign(campaignLocal);

        if (campaignStatusLocal === 'PROCESSING') {
            setCampaignStatus(campaignStatusLocal)
        } else {
            setCampaignStatus(campaignStatusLocal)
            setErrorMessage('We encountered an error while submitting your campaign. Please try again.')
        }
    }

    const getCampaignAndPollStatus_local = async () => {
        setIsLoadingCampaign(true);

        await getCampaignProcessingStatus();

        setIsLoadingCampaign(false);

        const playbookType: PlaybookType | CampaignType = campaign?.campaign_metadata?.playbook! || campaign?.campaign_metadata?.campaign_type!;
        const adPlatformLocal = PLAYBOOK_TYPE_INFO[playbookType]?.adPlatform;
        setAdPlatform(adPlatformLocal); // NOTE: not used yet, but might be useful in the future
    }

    // Setup polling effect
    useEffect(() => {
        let intervalId: NodeJS.Timeout | null = null;
        if (!isEmpty(campaignId) && !isNaN(campaignId)) {
            getCampaignAndPollStatus_local();

            intervalId = setInterval(() => {
                if (campaignStatusRef.current === 'PROCESSING') {
                    getCampaignProcessingStatus();
                }
            }, 1000);
        } else {
            history.push('/marketing');
        }

        // Cleanup function to clear the interval when component unmounts
        return () => {
            if (intervalId) {
                clearInterval(intervalId);
                intervalId = null;
            }
        };
    }, [campaignId]);

    const renderError = () => {
        if (!errorMessage || errorMessage === '') {
            return null;
        }
        return (
            <div className="errorContainer" style={{ color: Colors.red }}>
                {errorMessage}
            </div>
        )
    }

    let title = 'Campaign Success';
    let campaignSuccess = false
    let message = '';

    if (isLoadingCampaign) {
        title = 'Submitting Your Campaign...'
    } else if (campaign) {
        const campaignType = campaign.campaign_metadata?.playbook;

        switch (campaign.status) {
            case CampaignStatus.ACTIVE:
            case CampaignStatus.IN_REVIEW:
            case CampaignStatus.TO_RETRY:
                title = 'Campaign Submitted!'
                campaignSuccess = true

                switch (campaignType) {
                    // case 'get_playlisted':
                    //     message = `Your campaign to pitch "${content.name}" to playlists has been submitted. Once approved, you will see campaign updates in the dashboard.`;
                    //     break;
                    case PlaybookType.INCREASE_VIDEO_VIEWS:
                        let formattedInput = campaign.campaign_metadata?.v2_campaign_input as YouTubeCampaignSubmissionType;
                        let content = formattedInput?.video;
                        message = `Your YouTube ads campaign for "${content.name}" has been submitted. Once it goes live, you will see campaign updates in the dashboard.`;
                        break;
                    case PlaybookType.GET_PLAYLISTED:
                        // TODO MEGH: This is a hack to get the content for the get_playlisted campaign
                        let formattedInput3 = campaign.campaign_metadata as any
                        let content3 = formattedInput3?.content;

                        message = `Your campaign to pitch "${content3.name}" to playlists has been submitted. Once approved, you will see campaign updates in the dashboard.`;
                        break;
                    case PlaybookType.GROW_PLAYLIST_FOLLOWERS:
                    case PlaybookType.INCREASE_MUSIC_STREAMS:
                        let formattedInput2 = campaign.campaign_metadata?.v2_campaign_input as FacebookCampaignSubmissionType;
                        let content2 = formattedInput2?.record;
                        let recordName = content2 ? `"${content2?.name}" ` : 'your release ';
                        message = `Your campaign to promote ${recordName} has been submitted. Once approved, you will see campaign updates in the dashboard.`;
                        break;
                    case PlaybookType.INCREASE_LINK_CLICKS:
                        message = 'Your campaign to increase link clicks has been submitted. Once approved by Instagram, it will start running automatically.';
                        break;
                    case PlaybookType.TIKTOK_BOOST_ENGAGEMENT:
                        message = 'Your campaign to boost your TikTok engagement has been submitted. Once approved by TikTok, it will start running automatically.';
                        break;
                    case PlaybookType.BOOST_INSTAGRAM_ENGAGEMENT:
                        message = 'Your campaign to boost your Instagram engagement has been submitted. Once approved by Instagram, it will start running automatically.';
                        break;
                    case PlaybookType.META_EMAIL_SIGNUPS:
                        message = 'Your campaign to collect email signups has been submitted. Once approved by Meta, it will start running automatically.';
                        break;
                    case PlaybookType.META_IG_SOUND:
                        message = 'Your campaign to promote your Instagram sound has been submitted. Once approved by Meta, it will start running automatically.';
                        break;
                    case PlaybookType.META_PRESAVES:
                        message = 'Your pre-save campaign has been submitted. Once approved by Meta, it will start running automatically.';
                        break;
                    case PlaybookType.META_TICKET_SALES:
                        message = 'Your ticket sales campaign has been submitted. Once approved by Meta, it will start running automatically.';
                        break;
                    case PlaybookType.TIKTOK_EXTERNAL_TRAFFIC:
                        message = 'Your campaign to drive link clicks through TikTok has been submitted. Once approved by TikTok, it will start running automatically.';
                        break;
                    case PlaybookType.TIKTOK_MUSIC_STREAMS:
                        message = 'Your campaign to increase music streams via TikTok has been submitted. Once approved by TikTok, it will start running automatically.';
                        break;
                    case PlaybookType.TIKTOK_SOUND:
                        message = 'Your campaign to promote your TikTok sound has been submitted. Once approved by TikTok, it will start running automatically.';
                        break;
                }
                break;
            default:
                title = ''
                break;
        }
    }

    const renderCampaignStatusContent = () => {
        if (isLoadingCampaign) {
            return (
                <Loader className="successCampaignPageLoader" title="Submitting Your Campaign..." />
            )
        } else {
            if (campaignSuccess) {
                return (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <Icon
                                isCircle
                                color={Colors.green}
                                icon="check"
                                className="successIcon"
                                size={60}
                            />
                            <p style={Sty.text18}>{message}</p>
                        </div>
                        <ButtonSymphony
                            className="successCampaignButton"
                            onClick={() => history.push(`/marketing/campaigns/${campaign?.id}`)}
                        >
                            View Campaign Analytics
                        </ButtonSymphony>
                    </>
                )
            }
        }
    }

    return (
        <CardSymphony
            className="successCampaignHeader"
            title={title}
            isNoPadding
        >
            {/* <DebugBox
                data={{
                    isLoadingCampaign,
                    campaignStatus,
                    campaign,
                }}
            /> */}
            {renderCampaignStatusContent()}
            {/* {renderError()} */}
        </CardSymphony>
    )
}

export default SuccessCampaign;